import type { ComponentProps, SyntheticEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Page } from 'react-pdf'

import type { MarkupSurface } from './types'
import type { MarkupEditorRenderSettings } from './viewport'

const PDF_DIMENSION_TOLERANCE = 0.5
const fileIdentities = new WeakMap<File, number>()
let nextFileIdentity = 1

const fileIdentity = (file: File) => {
  const identity = fileIdentities.get(file) ?? nextFileIdentity++
  fileIdentities.set(file, identity)
  return identity
}

type PdfPage = Parameters<
  NonNullable<ComponentProps<typeof Page>['onLoadSuccess']>
>[0]

export function useMarkupEditorSurface(
  surface: MarkupSurface,
  renderSettings: MarkupEditorRenderSettings | undefined,
  reportError: (message?: string) => void
) {
  const identity = JSON.stringify([surface.kind, surface.id, surface.resetKey])
  const imageSourceKey =
    surface.kind === 'image'
      ? `${identity}-${surface.src}-${surface.width}-${surface.height}`
      : undefined
  const pdfGeometryKey =
    surface.kind === 'pdf-page'
      ? `${identity}-${fileIdentity(surface.file)}-${surface.pageNumber}-${surface.width}-${surface.height}-${surface.rotationDegrees}`
      : undefined
  const sourceKey = pdfGeometryKey ?? imageSourceKey
  const pageRenderKey = renderSettings
    ? `${surface.kind}-${sourceKey}-${renderSettings.pageWidth}-${renderSettings.devicePixelRatio}`
    : undefined
  const currentKeys = useRef({ sourceKey, pageRenderKey })
  currentKeys.current = { sourceKey, pageRenderKey }
  const verifiedPdfKey = useRef<string>()
  const previousPdfKey = useRef(pdfGeometryKey)
  if (previousPdfKey.current !== pdfGeometryKey) {
    previousPdfKey.current = pdfGeometryKey
    verifiedPdfKey.current = undefined
  }
  const [readyKey, setReadyKey] = useState<string>()
  const ready =
    surface.kind === 'image'
      ? Boolean(imageSourceKey && readyKey === imageSourceKey)
      : readyKey === pageRenderKey && verifiedPdfKey.current === pdfGeometryKey

  const previousSourceKey = useRef(sourceKey)
  useEffect(() => {
    if (surface.kind === 'pdf-page') setReadyKey(undefined)
    if (previousSourceKey.current === sourceKey) return
    previousSourceKey.current = sourceKey
    setReadyKey(undefined)
    verifiedPdfKey.current = undefined
  }, [pageRenderKey, sourceKey, surface.kind])

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    if (currentKeys.current.sourceKey !== imageSourceKey) return
    const { naturalWidth, naturalHeight } = event.currentTarget
    if (naturalWidth !== surface.width || naturalHeight !== surface.height) {
      setReadyKey(undefined)
      reportError(
        'The image dimensions changed. Reload this source before editing.'
      )
      return
    }
    reportError()
    if (imageSourceKey) setReadyKey(imageSourceKey)
  }

  const handleImageError = () => {
    if (currentKeys.current.sourceKey !== imageSourceKey) return
    setReadyKey(undefined)
    reportError(
      'The image surface could not be loaded. Reload the source and try again.'
    )
  }

  const handlePdfPageLoad = (page: PdfPage) => {
    if (
      surface.kind !== 'pdf-page' ||
      !pdfGeometryKey ||
      currentKeys.current.sourceKey !== pdfGeometryKey ||
      previousPdfKey.current !== pdfGeometryKey
    )
      return
    const viewport = page.getViewport({
      scale: 1,
      rotation: surface.rotationDegrees,
    })
    if (
      Math.abs(viewport.width - surface.width) > PDF_DIMENSION_TOLERANCE ||
      Math.abs(viewport.height - surface.height) > PDF_DIMENSION_TOLERANCE
    ) {
      verifiedPdfKey.current = undefined
      setReadyKey(undefined)
      reportError(
        'The displayed PDF page size does not match its rotated page. Reload the page source with the correct rotation before editing.'
      )
      return
    }
    verifiedPdfKey.current = pdfGeometryKey
    reportError()
  }

  const handlePdfLoadError = (documentError = false) => {
    if (currentKeys.current.sourceKey !== pdfGeometryKey) return
    reportError(
      documentError
        ? 'The PDF document could not be loaded. Check the file and try again.'
        : 'The PDF page could not be rendered. Check the file and try again.'
    )
  }

  const handlePdfRenderError = () => {
    if (
      currentKeys.current.sourceKey === pdfGeometryKey &&
      currentKeys.current.pageRenderKey === pageRenderKey
    )
      reportError(
        'The PDF page could not be rendered. Check the file and try again.'
      )
  }

  const handlePdfRenderSuccess = () => {
    if (
      currentKeys.current.sourceKey !== pdfGeometryKey ||
      currentKeys.current.pageRenderKey !== pageRenderKey ||
      verifiedPdfKey.current !== pdfGeometryKey ||
      !pageRenderKey
    )
      return
    setReadyKey(pageRenderKey)
  }

  return {
    imageSourceKey,
    sourceKey,
    pageRenderKey,
    ready,
    handleImageLoad,
    handleImageError,
    handlePdfPageLoad,
    handlePdfLoadError,
    handlePdfRenderError,
    handlePdfRenderSuccess,
  }
}
